import type { ExploreSearchParams } from "@/routes/explore/index";

import {
  type DifficultyEnum,
  GameStatusFilterEnum,
  IncludeEnum,
  type KindEnum,
  type PrivacyEnum,
  SortEnum,
  type VariablesFiltersType,
  type VariablesType,
} from "../types";

const VIRTUAL_CATEGORIES = [
  "escape-room/virtual",
  "escape-room/tabletop",
  "other/virtual",
  "other/online",
];

export function showOnMap(subcategories: string[]) {
  if (!subcategories.length) {
    return true;
  }

  return !!subcategories.filter(
    (subcategory) => !VIRTUAL_CATEGORIES.includes(subcategory)
  ).length;
}

// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: legacy
export default function apiVariables(
  params: ExploreSearchParams,
  overrides: Omit<VariablesType, "isUser"> & { isUser?: boolean } = {}
): VariablesType {
  const variables: VariablesType = {
    client: "webapp",
    filters: {},
    isUser: false,
  };

  const boundingBox = params.boundingBox;
  const coupons = params.coupons;
  const categories = params.categories?.split(",");
  const difficulty = params.difficulty?.split(",");
  const duration = params.duration;
  const hasBookings = params.has_bookings;
  const include = params.include;
  const isScary = params.is_scary;
  const kind = params.kind?.split(",");
  const location = params.location;
  const players = params.players;
  const privacy = params.privacy?.split(",");
  const searchQuery = params.query;
  const sortBy = params.sortBy as SortEnum | undefined;
  const status = params.status;
  const subcategories = params.subcategories?.split(",");
  const xmasTag = params.xmas_tag;

  if (boundingBox) {
    const [neLat, neLng, swLat, swLng] = (boundingBox as string)
      .split(",")
      .map((p) => Number.parseFloat(p));
    variables.boundingBox = { neLat, neLng, swLat, swLng };
  }

  if (coupons === "true") {
    (variables.filters as VariablesFiltersType).withCouponsOnly = true;
  }

  if (difficulty) {
    (variables.filters as VariablesFiltersType).difficulty =
      difficulty as DifficultyEnum[];
  }

  if (duration) {
    let minutesGte: number | undefined;
    let minutesLte: number | undefined;

    if (duration === "-60") {
      minutesLte = 59;
    } else if (duration === "60") {
      minutesGte = 60;
      minutesLte = 60;
    } else if (duration === "60+") {
      minutesGte = 61;
    } else if (duration === "120+") {
      minutesGte = 120;
      minutesLte = 240;
    } else if (duration === "240+") {
      minutesGte = 241;
    }

    if (minutesGte !== undefined) {
      (variables.filters as VariablesFiltersType).minutesGte = minutesGte;
    }

    if (minutesLte !== undefined) {
      (variables.filters as VariablesFiltersType).minutesLte = minutesLte;
    }
  }

  if (hasBookings) {
    (variables.filters as VariablesFiltersType).hasBookings = true;
  }

  if (location) {
    const [lat, lng] = location.split(",");
    variables.lat = Number.parseFloat(lat);
    variables.lng = Number.parseFloat(lng);
  }

  if (isScary === "Yes") {
    (variables.filters as VariablesFiltersType).isScary = true;
  }
  if (isScary === "No") {
    (variables.filters as VariablesFiltersType).isScary = false;
  }
  if (xmasTag === "Yes") {
    (variables.filters as VariablesFiltersType).tags = ["christmas"];
  }
  if (kind) {
    (variables.filters as VariablesFiltersType).kind = kind as KindEnum[];
  }
  if (players) {
    (variables.filters as VariablesFiltersType).players = Number.parseInt(
      players,
      10
    );
  }
  if (privacy) {
    (variables.filters as VariablesFiltersType).privacy =
      privacy as PrivacyEnum[];
  }
  if (searchQuery) {
    variables.query = searchQuery;
  }

  if (
    sortBy &&
    (sortBy === SortEnum.COMMUNITY_SCORE || sortBy === SortEnum.DISTANCE)
  ) {
    variables.sortBy = sortBy;

    if (variables.sortBy === SortEnum.COMMUNITY_SCORE) {
      variables.groupByLocation = false;
    }
  }
  // Always filter to open/coming soon unless user explicitly wants closed games
  // This applies to both text searches and browsing
  if (!(status && status === GameStatusFilterEnum.PERMANENTLY_CLOSED)) {
    (variables.filters as VariablesFiltersType).status = [
      GameStatusFilterEnum.COMING_SOON,
      GameStatusFilterEnum.OPEN,
    ];
  }

  if (subcategories?.length) {
    (variables.filters as VariablesFiltersType).categorySlugs = subcategories;
  } else if (categories?.length) {
    (variables.filters as VariablesFiltersType).categorySlugs = categories;
  }

  if (
    variables.sortBy === SortEnum.COMMUNITY_SCORE &&
    !variables.query &&
    (!include || include === IncludeEnum.NOT_PLAYED) &&
    showOnMap(subcategories || [])
  ) {
    variables.distance = 50;
  }

  return { ...variables, ...overrides };
}
